import { WorkspaceDataType } from "../../FpFundBrain/Chat2/services/workspaceInterface";
import { BASE_API_URL } from "../../../constants";
import { updateFpAppV4Data } from "../../../redux/fpAppV4Slice";
import { RootState } from "../../../redux/store";
import fpFetch from "../../../services/fpFetch";
import { flushPendingSaves } from "../../FpAppV4/services/saveWorkspaceContent";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useDispatch, useStore } from "react-redux";
import { useSelector } from "react-redux";

const fetchCanUpdate = async (workspaceId: string): Promise<boolean> => {
  try {
    const response = await fpFetch(
      `${BASE_API_URL}/workspaces/canUpdate?_id=${workspaceId}`
    );
    const data = await response.json();
    return !!data.canUpdate;
  } catch (error) {
    console.error("Error fetching canUpdate:", error);
    return false;
  }
};

// The find read says whether the caller can edit (#12239); an older backend
// does not send the field, so fall back to the separate call.
const resolveCanUpdate = (
  data: WorkspaceDataType,
  workspaceId: string
): Promise<boolean> =>
  typeof data.can_update === "boolean"
    ? Promise.resolve(data.can_update)
    : fetchCanUpdate(workspaceId);

const WORKSPACE_NOT_FOUND = "WORKSPACE_NOT_FOUND";
// react-query's own default when the client sets no retry policy.
const DEFAULT_MAX_RETRIES = 3;

type ClientRetry =
  | boolean
  | number
  | ((failureCount: number, error: Error) => boolean)
  | undefined;

// Apply the QueryClient's configured retry policy, so the only change this
// hook makes to it is the not-found carve-out.
const clientRetries = (
  retry: ClientRetry,
  failureCount: number,
  error: Error
): boolean => {
  if (typeof retry === "function") return retry(failureCount, error);
  if (typeof retry === "boolean") return retry;
  return failureCount < (retry ?? DEFAULT_MAX_RETRIES);
};

export interface UseWorkspaceDataOptions {
  // False skips the read entirely. Pass this rather than an undefined id,
  // which falls back to the Redux workspace id.
  enabled?: boolean;
  // False for observers that only show workspace metadata (title, owner,
  // style): they mount beside a content reader that already read it, and
  // re-reading on their mount only repeats that read. Content readers keep
  // the default: autosave does not update this cache, so an editor must
  // never initialize from content a newer save has overtaken.
  refetchOnMount?: boolean;
}

export const useWorkspaceData = (
  workspaceIdParam?: string,
  options?: UseWorkspaceDataOptions
) => {
  const workspaceIdFromRedux = useSelector(
    (state: RootState) => state.fpAppV4Data.workspaceId
  );
  const isTest = useSelector((state: RootState) => state.fpAppV4Data.isTest);
  const dispatch = useDispatch();
  // The store whose Provider this hook renders under — NOT the app-store
  // module singleton, which a test/story Provider never writes to (#11548
  // r19: reading the singleton made the branch below untestable and would
  // desync under any Provider override).
  const reduxStore = useStore<RootState>();
  const defaultRetry: ClientRetry =
    useQueryClient().getDefaultOptions().queries?.retry;

  // Use parameter if provided, otherwise fall back to Redux state
  const workspaceId = workspaceIdParam || workspaceIdFromRedux;
  const isValidId =
    workspaceId &&
    workspaceId !== "null" &&
    workspaceId !== "undefined" &&
    workspaceId !== "";

  const query = useQuery<WorkspaceDataType, Error>({
    queryKey: ["workspaceData", workspaceId],
    queryFn: async () => {
      if (!isValidId) return {} as WorkspaceDataType; // Prevent fetch with invalid ID
      await flushPendingSaves();
      const response = await fpFetch(
        `${BASE_API_URL}/workspaces/find?_id=${workspaceId}&saveContentRevision=true`
      );
      const data = await response.json();

      // Check if workspace exists - backend returns empty object {} when workspace doesn't exist
      if (!data || Object.keys(data).length === 0 || !data._id) {
        throw new Error(WORKSPACE_NOT_FOUND);
      }

      // Always update critical fields like type and workspaceId, even if editorContentLoaded is true
      // This ensures Redux state is correct when switching between modes (e.g., chat to report)
      const currentState = reduxStore.getState().fpAppV4Data;
      const needsTypeUpdate =
        !currentState?.type ||
        currentState.type !== data?.type ||
        currentState.workspaceId !== workspaceId;

      if (currentState?.editorContentLoaded && !needsTypeUpdate) {
        return data;
      }

      // If editorContentLoaded is true but we need to update type, do a
      // minimal update — but editability must still track the workspace
      // being switched to: a chat conversation stamps editorContentLoaded,
      // and without this the chat's document panel mounted read-only
      // (#11548 r19).
      if (currentState?.editorContentLoaded && needsTypeUpdate) {
        dispatch(
          updateFpAppV4Data({
            workspaceId: workspaceId,
            type: data?.type,
            isEditable: await resolveCanUpdate(data, workspaceId),
          })
        );
        return data;
      }

      if (isTest) {
        return data;
      }

      const canUpdate = await resolveCanUpdate(data, workspaceId);
      const mappedQueryAnswers = (data?.message_pairs || []).map(
        (item: any) => ({
          toolResponse: item,
          qid: item._id,
          added: true,
          tableData: item.tableData,
          status: item.status,
          toolId: item.agent,
          query: item.query,
          company: item.company,
        })
      );
      dispatch(
        updateFpAppV4Data({
          workspaceId: workspaceId,
          queryAnswers: mappedQueryAnswers,
          editorContentLoaded: true,
          title: data?.title,
          type: data?.type,
          queries: data?.queries,
          isEditable: canUpdate,
          showEditor: true,
          user_email: data?.user_email,
          user_id: data?.user_id,
          defaultQueryInputTree: data?.meta?.defaultFileTreeScope,
          defaultSelectedFileIds: data?.meta?.defaultSelectedFileIds,
        })
      );

      return data;
    },
    // Only run if valid (must be boolean)
    enabled: !!isValidId && (options?.enabled ?? true),
    // A missing (or deleted) workspace will not appear on a retry; show the
    // not-found state at once instead of after ~7 s of backoff (#12239).
    // Any other failure keeps the client's own retry policy.
    retry: (failureCount: number, error: Error) =>
      error.message !== WORKSPACE_NOT_FOUND &&
      clientRetries(defaultRetry, failureCount, error),
    gcTime: 0,
    staleTime: 0,
    refetchOnMount: options?.refetchOnMount ?? true,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
  });

  return query;
};

export async function fetchWorkspace(workspaceId: string) {
  const response = await fpFetch(
    `${BASE_API_URL}/workspaces/find?_id=${workspaceId}`
  );
  const data = await response.json();

  // Check if workspace exists - backend returns empty object {} when workspace doesn't exist
  if (!data || Object.keys(data).length === 0 || !data._id) {
    throw new Error("WORKSPACE_NOT_FOUND");
  }

  return data as WorkspaceDataType;
}
